import { useEffect, useRef, useState } from 'react';

import { Stack } from '@mui/material';
import { useStore } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { z } from 'zod';

import {
  projectFinancialsDefaultValues,
  ProjectFinancialsForm,
  ProjectFinancialsFormFields,
  projectFinancialsSchema,
} from '../forms/ProjectFinancials';
import type { RegisterProjectExpenseFormData } from '../types';
import { generateProjectExpenseSuccessRedirectUrl } from '../utils';
import { useCreateProjectExpense } from '@/api/REST/projectExpenses';
import { useGetUserMe } from '@/api/REST/userMe';
import { useGetCurrencyCodeOptionByIsoCode } from '@/api/typesense/currencyCodes';
import { useGetCustomerOptionById } from '@/api/typesense/customers';
import { useGetEmployeeOptionById } from '@/api/typesense/employees';
import { useGetDefaultPaymentMethodOption } from '@/api/typesense/paymentMethods';
import { useGetProjectOptionForProjectExpenseById } from '@/api/typesense/projects';
import { useGetProjectSubcontractOptionById } from '@/api/typesense/projectSubcontracts';
import { PageTitle } from '@/components/global/PageTitle';
import { Panels } from '@/components/layouts/Panels';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAppForm } from '@/forms/useAppForm';
import { useHasPermissionToAccessPage } from '@/hooks/useHasPermissionToAccessPage';
import { useUserAccountHasFeature } from '@/hooks/useUserAccountHasFeature';
import { ExpenseSummaryHeader } from '@/pages/register/expenses/components/ExpenseSummaryHeader';
import {
  expenseDetailsDefaultValues,
  ExpenseDetailsForm,
  expenseDetailsSchema,
  type ExpenseDetailsFormFields,
} from '@/pages/register/expenses/forms/ExpenseDetails';
import {
  ExpenseFileAttachments,
  type ExpenseFileAttachmentsHandle,
} from '@/pages/register/expenses/forms/ExpenseFileAttachments';
import {
  projectInformationDefaultValues,
  ProjectInformationForm,
  projectInformationSchema,
  type ProjectInformationFormFields,
} from '@/pages/register/expenses/forms/ProjectInformation';
import { useExpenseFormBlurredValuesStore } from '@/pages/register/expenses/stores/expenseFormBlurredValues';
import {
  calculateExchangeRate,
  generateSpecialCaseErrorMessage,
} from '@/pages/register/expenses/utils';

export const NewProjectExpense = () => {
  const { t } = useTranslation('expenses');
  const formRef = useRef<HTMLFormElement>(null);
  const attachmentRef = useRef<ExpenseFileAttachmentsHandle>(null);

  const { id: projectIdToPreselect } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const contractIdToPreselect = searchParams.get('contractId');

  const [attachedExpenseFiles, setAttachedExpenseFiles] = useState<string[]>(
    [],
  );
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);

  const {
    hasPermission: hasProjectAllExpensesPermission,
    isLoading: isProjectAllExpensesPermissionLoading,
  } = useHasPermissionToAccessPage('ProjectAllExpenses');

  const { reset: resetBlurredValueStore } = useExpenseFormBlurredValuesStore();

  useEffect(() => {
    resetBlurredValueStore();

    // Cleanup function to reset when component unmounts
    return () => {
      resetBlurredValueStore();
    };
  }, []);

  const { data: userMeData } = useGetUserMe();

  const {
    hasFeature: hasProfitOnExpenses,
    isLoading: isHasProfitOnExpensesLoading,
  } = useUserAccountHasFeature('profitOnExpenses');

  const {
    data: userCurrencySelectOption,
    isLoading: isUserCurrencySelectOptionLoading,
  } = useGetCurrencyCodeOptionByIsoCode(userMeData?.legalEntity.currencyCode);

  const { data: userEmployeeOption, isLoading: isUserEmployeeOptionLoading } =
    useGetEmployeeOptionById(userMeData?.userId ?? undefined);

  const {
    data: preSelectedProjectOption,
    isLoading: isPreSelectedProjectOptionLoading,
  } = useGetProjectOptionForProjectExpenseById(
    projectIdToPreselect ? Number(projectIdToPreselect) : undefined,
  );

  const {
    data: customerOptionOfPreselectedProject,
    isLoading: isCustomerOptionOfPreselectedProjectLoading,
  } = useGetCustomerOptionById(preSelectedProjectOption?.customerId);

  const {
    data: preSelectedContractOption,
    isLoading: isPreSelectedContractOptionLoading,
  } = useGetProjectSubcontractOptionById(
    contractIdToPreselect ? Number(contractIdToPreselect) : undefined,
  );

  const { data: defaultPaymentMethod } =
    useGetDefaultPaymentMethodOption('project');

  const createProjectExpense = useCreateProjectExpense();
  const successUrlRef = useRef<string>('');

  const form = useAppForm({
    defaultValues: {
      expense_details: {
        ...expenseDetailsDefaultValues,
        expenseCurrency:
          !isUserCurrencySelectOptionLoading && userCurrencySelectOption
            ? userCurrencySelectOption
            : null,
        paymentMethod: defaultPaymentMethod ?? null,
      },
      project_information: {
        ...projectInformationDefaultValues,
        employee:
          !isUserEmployeeOptionLoading && userEmployeeOption
            ? userEmployeeOption
            : null, // we still set employee here, just so the form field is valid, but we hide it and do not use it for expense creation
        ...(!isCustomerOptionOfPreselectedProjectLoading &&
          customerOptionOfPreselectedProject && {
            customer: customerOptionOfPreselectedProject,
          }),
        ...(!isPreSelectedProjectOptionLoading &&
          preSelectedProjectOption && {
            project: preSelectedProjectOption,
          }),
        ...(!isPreSelectedContractOptionLoading &&
          preSelectedContractOption && {
            contract: preSelectedContractOption,
          }),
      },
      project_financials: {
        ...projectFinancialsDefaultValues,
        ...(!isHasProfitOnExpensesLoading &&
          !hasProfitOnExpenses && {
            profitPercentage: '0',
            billableAmount: '0',
          }),
      },
    },
    validators: {
      onChange: z.object({
        expense_details: expenseDetailsSchema,
        project_information: projectInformationSchema,
        project_financials: projectFinancialsSchema,
      }),
    },
    onSubmit: async ({
      value,
      meta,
    }: {
      value: {
        expense_details: ExpenseDetailsFormFields;
        project_information: ProjectInformationFormFields;
        project_financials: ProjectFinancialsFormFields;
      };
      meta?: { saveAndNew?: boolean };
    }) => {
      const payload = {
        expense_details: value.expense_details,
        project_information: value.project_information,
        project_financials: value.project_financials,
        // when targetCurrencyConvertedAmount is not set, we do not have a diverging currency, so exchange rate should calculate to 100
        exchangeRate: calculateExchangeRate(
          value.expense_details.amountIncludingVat,
          value.expense_details.targetCurrencyConvertedAmount
            ? value.expense_details.targetCurrencyConvertedAmount
            : value.expense_details.amountIncludingVat,
        ),
        legalEntityCurrencyIsoCode: userCurrencySelectOption
          ? userCurrencySelectOption.isoCode
          : '',
        attachedExpenseFiles: attachedExpenseFiles,
      } as RegisterProjectExpenseFormData;

      await createProjectExpense.mutateAsync(payload, {
        onSuccess: (response) => {
          const fromProjectAllExpenses =
            projectIdToPreselect !== undefined &&
            projectIdToPreselect !== '' &&
            contractIdToPreselect === null;
          successUrlRef.current = generateProjectExpenseSuccessRedirectUrl(
            {
              projectId: response.projectId?.toString(),
              contractId: response.projectSubContractId?.toString(),
              employeeUserId: response.employeeUserId?.toString(),
              paymentMethodId: response.paymentMethodId?.toString(),
              date: response.date,
            },
            {
              projectAccess: response.hasProjectAccess,
              contractAccess: response.hasProjectContractAccess,
              projectAllExpensesTabPageAccess: hasProjectAllExpensesPermission,
            },
            fromProjectAllExpenses,
          );

          if (meta && meta.saveAndNew) {
            const formDataToKeep = form.getFieldValue('project_information');
            form.reset();
            attachmentRef.current?.reset();
            // Reset with new default values is not working as expected, so we manually set the values to keep
            form.setFieldValue('project_information', formDataToKeep);
          }
        },
      });
    },
    onSubmitInvalid: () => {
      // this error throwing is important to let SaveFormActions know that the submission failed
      throw new Error('Form is invalid');
    },
  });

  const handleSubmitAndNew = async () => {
    await form.handleSubmit({ saveAndNew: true });
  };

  const handleResetForm = () => {
    form.reset();
    attachmentRef.current?.reset();
  };

  // we need this to set the billable checkbox based on the default value of the selected contract
  const selectedContract = useStore(
    form.store,
    (state) => state.values.project_information.contract,
  );

  useEffect(() => {
    if (selectedContract) {
      form.setFieldValue(
        'expense_details.billable',
        selectedContract.isBillable,
      );
    }
  }, [selectedContract]);

  return (
    <>
      <PageTitle title={t('newProjectExpense.PageTitle')} />
      <Panels autoSaveId="expense-panels">
        <Panels.Main panelContentProps={{ constrainWidth: true }}>
          <form.Subscribe selector={(state) => state.values}>
            {(val) => (
              <PageHeader
                title={t('newProjectExpense.PageTitle')}
                subtitle={t('newProjectExpense.PageSubtitle')}
              >
                <PageHeader.Info>
                  <ExpenseSummaryHeader
                    expenseCurrency={val.expense_details.expenseCurrency}
                    paymentMethod={val.expense_details.paymentMethod}
                    supplier={val.expense_details.supplier?.value}
                    category={val.expense_details.expenseType?.value}
                    expenseType="project"
                  />
                </PageHeader.Info>
              </PageHeader>
            )}
          </form.Subscribe>
          <form.Form ref={formRef}>
            <Stack gap={{ xs: 2, sm: 4 }}>
              <ProjectInformationForm
                title={t('projectInformation.FormTitle')}
                fields="project_information"
                expenseType="project"
                form={form}
              />

              <ExpenseDetailsForm
                title={t('expenseDetails.FormTitle')}
                fields="expense_details"
                form={form}
                expenseType="project"
                expenseTargetCurrency={userCurrencySelectOption ?? null}
              />

              <ProjectFinancialsForm
                title={t('projectFinancials.FormTitle')}
                fields="project_financials"
                form={form}
                expenseTargetCurrency={userCurrencySelectOption ?? null} // on new project expense, the base expenseTargetCurrency is always the current user's legal entity currency
              />

              <form.SaveFormActions
                data-automation-id="new-project-expense-save-form-actions"
                form={form}
                formRef={formRef}
                isSubmitDisabled={
                  isUploadingFiles || isProjectAllExpensesPermissionLoading
                }
                successNavigationUrl={() => successUrlRef.current}
                onReset={handleResetForm}
                onSubmit={form.handleSubmit}
                onSubmitAndNew={handleSubmitAndNew}
                getErrorMessageFromError={generateSpecialCaseErrorMessage}
              />
            </Stack>
          </form.Form>
        </Panels.Main>
        <Panels.Secondary panelContentProps={{ hideOverflow: true }}>
          <ExpenseFileAttachments
            ref={attachmentRef}
            expenseType="project"
            onFilesUploading={setIsUploadingFiles}
            onAttachedExpenseFilesChanged={setAttachedExpenseFiles}
          />
        </Panels.Secondary>
      </Panels>
    </>
  );
};
